import React, { useEffect, useState } from 'react';

import { isArray } from 'lodash';

// components
import MapChartHealth from 'components/charts/mapChartHealth/mapChartHealth';
import DatasetComboHealth from 'components/charts/datasetComboHealth/datasetComboHealth';
import { ChartComboItem } from 'components/charts/chartCombo/chartCombo';
import IWPSlider from 'components/charts/slider/slider';
import ChartInfoButton from 'components/charts/chartInfoButton/chartInfoButton';
import ChartResetButton from 'components/charts/chartResetButton/chartResetButton';
import ChartTooltip from 'components/charts/chartTooltip/chartTooltip';
import ChartActionsMobile from 'components/charts/chartActionsMobile/chartActionsMobile';

// utils
import chartsUtils, { DataFile } from 'utils/charts.utils';
import ChartLegend from 'components/charts/chartLegend/chartLegend';

interface Props {
  dataFiles: DataFile[];
  titlePrefix: string;
}

const MapChartHealthWrapper = (props: Props) => {
  const [chartRef, setChartRef] = useState<any>();
  const [noDataYear, setNoDataYear] = useState<number>(0);

  const incomeGroups = [
    { text: 'Unterste Einkommen', value: '0' },
    { text: 'Unterer Mittelstand', value: '1' },
    { text: 'Oberer Mittelstand', value: '2' },
    { text: 'Oberste Einkommen', value: '3' },
  ];

  const defaultIncomeGroup = incomeGroups[0];
  const [incomeGroup, setIncomeGroup] = useState<ChartComboItem>(defaultIncomeGroup);

  const dataFile = props.dataFiles[parseInt(incomeGroup.value)];

  const defaultYear = chartsUtils.getMaxYearForData(dataFile);
  const [year, setYear] = useState<number>(defaultYear);

  const isResetButtonDisabled = incomeGroup.value === defaultIncomeGroup.value && year === defaultYear;

  const handleDatasetChange = (value: ChartComboItem) => {
    setIncomeGroup(value);
  };

  const handleChangeYear = (value: number | number[]) => {
    setNoDataYear(0);
    if (!isArray(value)) {
      setYear(value);
    } else {
      setYear(value[0]);
    }
  };

  const handleReset = () => {
    setIncomeGroup(incomeGroups[0]);
    setNoDataYear(0);
    setYear(chartsUtils.getMaxYearForData(props.dataFiles[0]));
  };

  const getNoDataNextYear = (year: number) => {
    return chartsUtils.getNextYearWithData(dataFile, year);
  };

  const handleNoData = () => {
    setNoDataYear(year);
    setYear((year) => getNoDataNextYear(year));
  };

  const handleCallback = (chartRef: any) => {
    setChartRef(chartRef);
  };

  const handleExport = () => {
    if (chartRef) {
      chartRef.current.chart.exportChartLocal();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setNoDataYear(0);
    }, 5000);
    return () => clearTimeout(timer);
  }, [noDataYear]);

  return (
    <div className="chart2">
      <div className="chartfilters-wrapper flex flex-wrap gap-x-12 gap-y-4">
        <div className="min-w-[150px] max-w-[200px] flex-1">
          <DatasetComboHealth options={incomeGroups} value={incomeGroup} onChange={handleDatasetChange}></DatasetComboHealth>
        </div>
        <div className="min-w-[200px] max-w-[300px] flex-1">
          <IWPSlider
            range={false}
            min={chartsUtils.getMinYearForData(dataFile)}
            max={chartsUtils.getMaxYearForData(dataFile)}
            onChange={handleChangeYear}
            value={year}
          />
          <ChartTooltip
            show={noDataYear > 0}
            text={`Sie haben ein Jahr ausgewählt, für das keine Daten verfügbar sind. Früher wurde die Steuergrundlage nicht jährlich bemessen. Ihnen wird das nächsthöhere verfügbare Jahr angezeigt.`}
          />
        </div>
        <div className="chartfilters-actions ml-auto">
          <div className="grid-row">
            <div className="grid-cell">
              <ChartResetButton onClick={handleReset} disabled={isResetButtonDisabled} />
            </div>
            <div className="grid-cell">
              <ChartInfoButton text="Geben Sie rechts an, für welches Jahr Sie sich interessieren. Wenn Sie die generierte Grafik herunterladen möchten, klicken Sie bitte auf das Pfeilsymbol links." />
            </div>
          </div>
        </div>
      </div>
      <div className="chart2-wrapper">
        <MapChartHealth
          dataFile={dataFile}
          title={`${props.titlePrefix} ${incomeGroup.text} ${year}`}
          year={year}
          options={{
            legend: {
              title: {
                text: '<span class="mapchart-legendtitle">Umverteilung (in CHF pro Kopf)</span>',
              },
            },
            tooltip: {
              headerLabel1: 'JAHR',
              headerLabel2: 'UMVERTEILUNG (in CHF pro Kopf)',
            },
          }}
          onNoData={handleNoData}
          onCallback={handleCallback}
        />
      </div>
      <ChartActionsMobile onExport={handleExport} onReset={handleReset} resetButtonDisabled={isResetButtonDisabled}></ChartActionsMobile>
      <ChartLegend onExport={handleExport} />
    </div>
  );
};

export default MapChartHealthWrapper;
